import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { 
  FiPlay, FiCheckCircle, FiLayers, FiSend, FiBarChart2, 
  FiPlus, FiFilter, FiMoreHorizontal, FiLoader, FiAlertTriangle, 
  FiCloudOff, FiCheck, FiEdit3, FiEye, FiClock, FiShare2,
  FiTarget, FiImage
} from 'react-icons/fi';

const workflowSteps = [
  { id: 1, title: "Campaign Strategy", desc: "Define goals on the Orbit map.", icon: FiTarget, action: "Assign Orbit" },
  { id: 2, title: "Asset Collection", desc: "Import visual assets to the library.", icon: FiImage, action: "Upload Files" },
  { id: 3, title: "Content Creation", desc: "Draft captions and schedule in Calendar.", icon: FiEdit3, action: "Open Editor" },
  { id: 4, title: "Review Flow", desc: "Submit for approval to team leads.", icon: FiSend, action: "Notify Team" },
  { id: 5, title: "Live Sync", desc: "Automatic publishing to all channels.", icon: FiLayers, action: "Check Sync" },
  { id: 6, title: "Insight Orbit", desc: "Track performance in real-time.", icon: FiBarChart2, action: "View Stats" }
];

const sampleData = [
  { id: 'C-001', name: "Q3 Product Launch", channel: "Multi", status: "Active", lead: "Sarah J.", date: "2024-08-15" },
  { id: 'C-002', name: "Summer Sale 2024", channel: "Instagram", status: "Scheduled", lead: "Mike T.", date: "2024-07-01" },
  { id: 'C-003', name: "Founder Series", channel: "LinkedIn", status: "Review", lead: "Elena R.", date: "2024-08-10" },
  { id: 'C-004', name: "Tech Weekly", channel: "Twitter", status: "Draft", lead: "John D.", date: "2024-09-05" },
];

export default function DemoPage() {
  const [activeTab, setActiveTab] = useState('workflow');
  const [currentStep, setCurrentStep] = useState(1);
  const [filter, setFilter] = useState('All');
  const [simulatedState, setSimulatedState] = useState('ideal');

  return (
    <div className="h-full flex flex-col p-4 md:p-8 overflow-y-auto max-w-7xl mx-auto space-y-8">
      
      {/* Tab Navigation */}
      <div className="flex gap-4 border-b border-[#1a1a1a] pb-4 sticky top-0 bg-[#050505] z-10">
        {['workflow', 'data', 'states'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-6 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab 
              ? 'bg-[#FF5A5F]/10 text-[#FF5A5F] border border-[#FF5A5F]/20' 
              : 'text-gray-500 hover:text-white'
            }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {activeTab === 'workflow' && (
          <motion.section 
            key="workflow"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-6"
          >
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold text-white">Try this flow</h3>
              <p className="text-sm text-gray-400">Step {currentStep} of {workflowSteps.length}</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-3">
                {workflowSteps.map((step) => (
                  <button
                    key={step.id}
                    onClick={() => setCurrentStep(step.id)}
                    className={`w-full flex items-center gap-4 p-4 rounded-xl border transition-all text-left group ${
                      currentStep === step.id 
                      ? 'border-[#FF5A5F] bg-[#FF5A5F]/5' 
                      : 'border-[#1a1a1a] bg-[#0a0a0a] hover:border-[#2a2a2a]'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                      currentStep === step.id ? 'bg-[#FF5A5F] text-white shadow-[0_0_15px_rgba(255,90,95,0.4)]' : 'bg-[#141414] text-gray-500 group-hover:text-white'
                    }`}>
                      <SafeIcon icon={step.icon} />
                    </div>
                    <div>
                      <h4 className={`text-sm font-medium ${currentStep === step.id ? 'text-white' : 'text-gray-400'}`}>{step.title}</h4>
                      <p className="text-xs text-gray-500 mt-1">{step.desc}</p>
                    </div>
                    {currentStep > step.id && (
                      <SafeIcon icon={FiCheckCircle} className="ml-auto text-[#FF5A5F]" />
                    )}
                  </button>
                ))}
              </div>

              <div className="glass-panel p-8 flex flex-col items-center justify-center text-center bg-gradient-to-br from-[#0a0a0a] to-[#111]">
                <div className="mb-6">
                  <div className="w-20 h-20 rounded-2xl bg-[#FF5A5F]/10 flex items-center justify-center mb-4 mx-auto">
                    <SafeIcon icon={workflowSteps[currentStep-1].icon} className="text-4xl text-[#FF5A5F] animate-pulse" />
                  </div>
                  <h4 className="text-lg font-bold text-white mb-2">{workflowSteps[currentStep-1].title}</h4>
                  <p className="text-sm text-gray-400 max-w-xs">{workflowSteps[currentStep-1].desc}</p>
                </div>
                <button className="btn-primary flex items-center gap-2">
                  <SafeIcon icon={FiPlay} size={14} />
                  {workflowSteps[currentStep-1].action}
                </button>
                <div className="mt-8 pt-8 border-t border-[#1a1a1a] w-full flex justify-around text-xs text-gray-500">
                  <div className="text-center"><span className="block text-white font-bold">100%</span> Automation</div>
                  <div className="text-center"><span className="block text-white font-bold">2.4m</span> Avg. Reach</div>
                  <div className="text-center"><span className="block text-white font-bold">Q3</span> Priority</div>
                </div>
              </div>
            </div>
          </motion.section>
        )}

        {activeTab === 'data' && (
          <motion.section 
            key="data"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-6"
          >
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <h3 className="text-xl font-semibold text-white">Sample Data</h3>
              <div className="flex gap-2">
                {['All', 'Active', 'Scheduled', 'Review'].map((f) => (
                  <button 
                    key={f}
                    onClick={() => setFilter(f)}
                    className={`px-3 py-1 rounded text-xs border ${filter === f ? 'border-[#FF5A5F] text-[#FF5A5F]' : 'border-[#1a1a1a] text-gray-500'}`}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>

            <div className="glass-panel overflow-hidden">
              <table className="w-full text-left">
                <thead className="bg-[#141414] border-b border-[#1a1a1a]">
                  <tr>
                    <th className="p-4 text-xs font-medium text-gray-400 uppercase tracking-wider">Campaign ID</th>
                    <th className="p-4 text-xs font-medium text-gray-400 uppercase tracking-wider">Name</th>
                    <th className="p-4 text-xs font-medium text-gray-400 uppercase tracking-wider">Channel</th>
                    <th className="p-4 text-xs font-medium text-gray-400 uppercase tracking-wider">Status</th>
                    <th className="p-4 text-xs font-medium text-gray-400 uppercase tracking-wider">Lead</th>
                    <th className="p-4 text-xs font-medium text-gray-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#1a1a1a]">
                  {sampleData
                    .filter(d => filter === 'All' || d.status === filter)
                    .map((item) => (
                    <tr key={item.id} className="hover:bg-[#141414] transition-colors group">
                      <td className="p-4 text-xs font-mono text-[#FF5A5F]">{item.id}</td>
                      <td className="p-4 text-sm text-white font-medium">{item.name}</td>
                      <td className="p-4 text-xs text-gray-400">
                        <span className="bg-[#1a1a1a] px-2 py-0.5 rounded">{item.channel}</span>
                      </td>
                      <td className="p-4">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full border ${
                          item.status === 'Active' ? 'border-green-500/20 bg-green-500/10 text-green-400' :
                          item.status === 'Scheduled' ? 'border-blue-500/20 bg-blue-500/10 text-blue-400' :
                          'border-yellow-500/20 bg-yellow-500/10 text-yellow-400'
                        }`}>
                          {item.status}
                        </span>
                      </td>
                      <td className="p-4 text-sm text-gray-400">{item.lead}</td>
                      <td className="p-4">
                        <div className="flex gap-2">
                          <button className="p-1.5 hover:bg-[#FF5A5F]/10 hover:text-[#FF5A5F] rounded transition-colors"><SafeIcon icon={FiEye} size={14}/></button>
                          <button className="p-1.5 hover:bg-[#FF5A5F]/10 hover:text-[#FF5A5F] rounded transition-colors"><SafeIcon icon={FiMoreHorizontal} size={14}/></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.section>
        )}

        {activeTab === 'states' && (
          <motion.section 
            key="states"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-6"
          >
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold text-white">Simulated States</h3>
              <div className="flex gap-2">
                <button onClick={() => setSimulatedState('ideal')} className={`px-3 py-1 rounded text-xs ${simulatedState === 'ideal' ? 'bg-[#FF5A5F] text-white' : 'bg-[#1a1a1a] text-gray-400'}`}>Ideal</button>
                <button onClick={() => setSimulatedState('empty')} className={`px-3 py-1 rounded text-xs ${simulatedState === 'empty' ? 'bg-[#FF5A5F] text-white' : 'bg-[#1a1a1a] text-gray-400'}`}>Empty</button>
                <button onClick={() => setSimulatedState('loading')} className={`px-3 py-1 rounded text-xs ${simulatedState === 'loading' ? 'bg-[#FF5A5F] text-white' : 'bg-[#1a1a1a] text-gray-400'}`}>Loading</button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              
              {/* Asset Library State */}
              <div className="glass-panel p-6 flex flex-col min-h-[250px]">
                <h4 className="text-sm font-medium text-gray-400 mb-6 flex items-center gap-2">
                  <SafeIcon icon={FiImage} /> Asset Library
                </h4>
                {simulatedState === 'empty' ? (
                  <div className="flex-1 flex flex-col items-center justify-center text-center">
                    <SafeIcon icon={FiCloudOff} className="text-4xl text-gray-700 mb-4" />
                    <p className="text-sm text-gray-500">No assets found in workspace.</p>
                    <button className="mt-4 text-xs text-[#FF5A5F] hover:underline">Upload first asset</button>
                  </div>
                ) : simulatedState === 'loading' ? (
                  <div className="flex-1 flex flex-col items-center justify-center">
                    <SafeIcon icon={FiLoader} className="text-3xl text-[#FF5A5F] animate-spin mb-4" />
                    <p className="text-xs text-gray-500">Syncing media library...</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    {[1,2,3,4,5,6].map(i => (
                      <div key={i} className="aspect-square rounded bg-[#1a1a1a] border border-[#2a2a2a] overflow-hidden group relative">
                        <img src={`https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?q=10&w=100&auto=format&fit=crop`} className="w-full h-full object-cover opacity-50 group-hover:opacity-100" />
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 bg-black/40"><SafeIcon icon={FiPlus} /></div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Approval Workflow State */}
              <div className="glass-panel p-6 flex flex-col min-h-[250px]">
                <h4 className="text-sm font-medium text-gray-400 mb-6 flex items-center gap-2">
                  <SafeIcon icon={FiCheckCircle} /> Approval Flow
                </h4>
                {simulatedState === 'empty' ? (
                  <div className="flex-1 flex flex-col items-center justify-center text-center">
                    <p className="text-sm text-gray-500">Queue is clear.</p>
                    <p className="text-[10px] text-gray-600 mt-1 uppercase tracking-widest">Great job, Elena!</p>
                  </div>
                ) : simulatedState === 'loading' ? (
                  <div className="space-y-4">
                    {[1,2].map(i => (
                      <div key={i} className="h-16 rounded bg-[#141414] animate-pulse"></div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="p-3 rounded-lg border border-yellow-500/20 bg-yellow-500/5 flex items-center gap-3">
                      <SafeIcon icon={FiAlertTriangle} className="text-yellow-500" />
                      <div className="flex-1">
                        <p className="text-xs font-medium text-white">Revision Required</p>
                        <p className="text-[10px] text-gray-500">Caption too long for Instagram</p>
                      </div>
                      <button className="text-[10px] text-[#FF5A5F] hover:bg-[#FF5A5F]/10 px-2 py-1 rounded">Edit</button>
                    </div>
                    <div className="p-3 rounded-lg border border-green-500/20 bg-green-500/5 flex items-center gap-3">
                      <SafeIcon icon={FiCheck} className="text-green-500" />
                      <div className="flex-1">
                        <p className="text-xs font-medium text-white">Q3 Video Ready</p>
                        <p className="text-[10px] text-gray-500">Scheduled for 12:00 PM</p>
                      </div>
                      <SafeIcon icon={FiShare2} className="text-gray-600" size={14} />
                    </div>
                  </div>
                )}
              </div>

            </div>
          </motion.section>
        )}
      </AnimatePresence>

      {/* Footer Branding */}
      <footer className="mt-auto pt-8 border-t border-[#1a1a1a] flex flex-col md:flex-row justify-between items-center gap-4 py-6">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded bg-[#FF5A5F]/10 border border-[#FF5A5F]/20 flex items-center justify-center">
             <SafeIcon icon={FiTarget} className="text-[#FF5A5F]" />
          </div>
          <div>
            <p className="text-sm font-bold text-white tracking-widest uppercase">ContentOrbit Demo</p>
            <p className="text-[10px] text-gray-500">Version 2.4.0-Demo</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-[10px] text-gray-600 uppercase tracking-[0.2em] font-medium">
            Made by <span className="text-gray-400">LingLink AI</span> and <span className="text-gray-400">DigLab AI</span>
          </p>
        </div>
      </footer>
    </div>
  );
}